import { Input, InputNumber } from '@seiue/ui'
import { findIndex } from '@seiue/util'
import { useAtomValue } from 'jotai'
import React, { useContext } from 'react'
import styled from 'styled-components'

import { CircledNumber } from 'packages/components/CircledNumber'
import { GrowpIcon } from 'packages/components/GrowpIcon'
import { RichTextEditor } from 'packages/components/RichText'
import { QuestionTypeEnum } from 'packages/sdks-next/growp'
import { precisionFormat } from 'packages/utils'

import { WaringText } from '@/components/Styled'
import { EditPaperContext } from '@/features/test-paper/pages/EditPaper/context'
import { JudgeMode } from '@/features/test-paper/types'
import { getTheme } from '@/utils/theme'

import { isValidQuestionFieldAtom } from '../../atom'
import { EditProductQuestionItemProps, PaperQuestionWithReq } from '../../types'
import {
  editSubQuestion,
  getSubQuestions,
  getTreeNodeId,
  validateFillInBlank,
} from '../../utils'

/**
 * 填空题答案 - 编辑
 *
 * @param props - 参数
 * @param props.curQuestion - 当前题目
 * @param props.mainQuestion - 主题干
 * @param props.subQuestions - 字子题目
 * @param props.treeNodeId - 树节点 id
 * @param props.setSubQuestions - 设置子题目
 * @returns React.ReactNode
 */
export const EditFullInBlankAnswer: React.FC<EditProductQuestionItemProps> = ({
  curQuestion,
  mainQuestion,
  subQuestions,
  treeNodeId,
  setSubQuestions,
}) => {
  const { paper, limitLevel } = useContext(EditPaperContext)
  const theme = getTheme()
  const isValidField = useAtomValue(isValidQuestionFieldAtom)
  const secondSubQuestions = getSubQuestions(
    subQuestions,
    curQuestion.id || treeNodeId || 0
  )

  const validate = validateFillInBlank(secondSubQuestions, !!paper.showScore)

  const getStandardAnswer = (question: PaperQuestionWithReq) => {
    if (question.standardAnswers) {
      return question.standardAnswers[0]
    }
    return undefined
  }

  const onChangesStandardAnswer = (
    question: PaperQuestionWithReq,
    v: string
  ) => {
    const preStandardAnswers = question.standardAnswers || []
    preStandardAnswers?.splice(0, 1, v)
    const newSubQuestions = editSubQuestion(
      subQuestions,
      question.treeNodeId || 0,
      {
        standardAnswers: preStandardAnswers,
      }
    )
    setSubQuestions(newSubQuestions)
  }
  return (
    <Wrapper>
      {secondSubQuestions.map((question, index) => {
        return (
          <QuestionItem key={question.treeNodeId || index}>
            <div>
              <MainAnswer>
                <CircledNumber number={index + 1} />
                {curQuestion?.settings?.judgeMode === JudgeMode.Manual ? (
                  <RichTextEditor
                    readOnly={limitLevel > 0}
                    minHeight={32}
                    value={getStandardAnswer(question)}
                    onChange={(v) => onChangesStandardAnswer(question, v)}
                    excludeKeys={[
                      'headerSelect',
                      'group-upload',
                      'insertLink',
                      'fullScreen',
                    ]}
                    insertKeys={{
                      index: 17,
                      keys: ['uploadImage', 'insertFormula'],
                    }}
                  />
                ) : (
                  <Input
                    disabled={limitLevel > 0}
                    addonAfter={
                      <div
                        style={{
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          // 初始添加了标准答案选项，还未填写答案，所以用空字符串表示
                          const curAnswers = question.standardAnswers || ['']
                          curAnswers.push('')
                          const newSubQuestions = editSubQuestion(
                            subQuestions,
                            question.treeNodeId || 0,
                            {
                              standardAnswers: curAnswers,
                            }
                          )
                          setSubQuestions(newSubQuestions)
                        }}
                      >
                        <GrowpIcon
                          name="Plus"
                          size={12}
                          text="备选答案"
                        />
                      </div>
                    }
                    value={getStandardAnswer(question)}
                    onChange={(e) => {
                      const v = e.target.value || ''
                      onChangesStandardAnswer(question, v)
                    }}
                  />
                )}
                {paper.showScore ? (
                  <InputNumber
                    disabled={limitLevel > 1}
                    stringMode={true}
                    addonAfter="分"
                    controls={false}
                    value={precisionFormat(question.score)}
                    onChange={(v) => {
                      const newSubQuestions = editSubQuestion(
                        subQuestions,
                        question.treeNodeId || 0,
                        {
                          score: v ? String(v) : undefined,
                        }
                      )
                      setSubQuestions(newSubQuestions)
                    }}
                  />
                ) : null}
                <div
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    const preSubQuestions = [...subQuestions]
                    const i = findIndex(
                      preSubQuestions,
                      (q) => q.treeNodeId === question.treeNodeId
                    )
                    preSubQuestions.splice(i, 1)
                    setSubQuestions(preSubQuestions)
                  }}
                >
                  <GrowpIcon
                    name="Delete"
                    size={16}
                  />
                </div>
              </MainAnswer>
              {validate || !isValidField ? null : (
                <ValidateMsgWrapper>
                  <WaringText>
                    {secondSubQuestions.length > 0 &&
                    !getStandardAnswer(question)
                      ? `答案不能为空`
                      : null}
                  </WaringText>
                  <WaringText>
                    {secondSubQuestions.length > 0 &&
                    !precisionFormat(question.score)
                      ? `分数不能为空`
                      : null}
                  </WaringText>
                </ValidateMsgWrapper>
              )}
            </div>
            {question.standardAnswers?.map((answer, answerIndex) => {
              if (answerIndex < 1) return
              return (
                <div key={`${index}${answerIndex}`}>
                  <SubAnswer
                    key={`${index}${answerIndex}`}
                    $showScore={!!paper.showScore}
                  >
                    <Input
                      disabled={limitLevel > 0}
                      value={answer}
                      onChange={(e) => {
                        const v = e.target.value || ''
                        const preAnswers = question.standardAnswers
                          ? [...question.standardAnswers]
                          : ['']
                        preAnswers.splice(answerIndex, 1, v ? v : '')
                        const newSubQuestions = editSubQuestion(
                          subQuestions,
                          question.treeNodeId || 0,
                          {
                            standardAnswers: preAnswers,
                          }
                        )
                        setSubQuestions(newSubQuestions)
                      }}
                    />
                    <div
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const curAnswers = question.standardAnswers || ['']
                        curAnswers.splice(answerIndex, 1)
                        const newSubQuestions = editSubQuestion(
                          subQuestions,
                          question.treeNodeId || 0,
                          {
                            standardAnswers: curAnswers,
                          }
                        )
                        setSubQuestions(newSubQuestions)
                      }}
                    >
                      <GrowpIcon
                        name="Delete"
                        size={16}
                      />
                    </div>
                  </SubAnswer>
                  <div style={{ paddingLeft: '30px' }}>
                    {validate || !isValidField ? null : (
                      <WaringText>
                        {secondSubQuestions.length > 0 && !answer
                          ? `答案不能为空`
                          : null}
                      </WaringText>
                    )}
                  </div>
                </div>
              )
            })}
          </QuestionItem>
        )
      })}
      <div
        onClick={() => {
          const treeNodeId = getTreeNodeId(
            mainQuestion ? [mainQuestion, ...subQuestions] : subQuestions
          )
          const newQuestion = {
            type: QuestionTypeEnum.FillInBlank,
            options: [],
            standardAnswers: [''],
            parentId: curQuestion.treeNodeId,
            treeNodeId: treeNodeId,
            treeNodePid: curQuestion.treeNodeId,
            weight: treeNodeId,
          } as any
          setSubQuestions([...subQuestions, newQuestion])
        }}
        style={{
          marginTop: isValidField ? '6px' : '0px',
          paddingLeft: secondSubQuestions.length > 0 ? '26px' : '0px',
          width: '110px',
          display: limitLevel > 0 ? 'none' : 'block',
        }}
      >
        <GrowpIcon
          name="Plus"
          size={14}
          text="添加选项"
          color={theme.brand._1}
          wrapperStyle={{
            cursor: 'pointer',
          }}
        />
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  &&& {
    .ant-input-group-addon {
      width: 20px;
    }
    .ant-input-number-group {
      width: 84px;
    }
    .ant-input-number-input {
      padding-right: 0;
    }
  }
`

const QuestionItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const MainAnswer = styled.div`
  width: 100%;
  min-height: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`

const SubAnswer = styled.div<{ $showScore: boolean }>`
  width: 100%;
  height: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: ${(props) => (props.$showScore ? '0 92px 0 26px' : '0 0 0 26px')};
`

const ValidateMsgWrapper = styled.div`
  width: 100%;
  padding: 0 30px;
  display: flex;
  justify-content: space-between;
  align-content: center;
`
